import * as LucideIcons from "lucide-react";
import { FormattedNumberInput } from "@shadcn-in-nx/ui/formatted-number-input";

export const Icon = (props) => {
    try {
        // Get the icon component, fallback to CircleHelp if not found
        const IconComponent = LucideIcons[props.name] || LucideIcons.CircleHelp;

        if (!IconComponent) {
            console.warn(`Icon "${props.name}" not found in lucide-react`);
            return null;
        }

        return <IconComponent {...props} />;
    } catch (error) {
        console.error(`Error rendering icon "${props.name}":`, error);
        return null;
    }
};

const FormField = ({
    label,
    id,
    type = "text",
    value,
    onChange,
    placeholder = "",
    as = "input",
    required = false,
    helpText = "",
    disabled = false,
    className: extraClassName = "",
    icon = "",
}) => {
    const commonProps = {
        id,
        name: id,
        value,
        onChange,
        placeholder,
        required,
        disabled,
        className: `block w-full ${
            icon ? "pl-10" : "pl-4"
        } pr-4 py-2 mt-2 text-gray-700 bg-white border border-gray-200 rounded-md focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 focus:outline-none focus:ring ${
            disabled ? "bg-gray-100" : ""
        } ${extraClassName}`,
    };

    return (
        <div className="relative">
            <label htmlFor={id} className="text-gray-700 font-medium">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {as === "textarea" ? (
                <div className="relative">
                    {icon && (
                        <div className="pointer-events-none absolute left-0 top-0 flex items-center pl-3 h-10">
                            <Icon
                                name={icon}
                                className="h-5 w-5 text-gray-400"
                            />
                        </div>
                    )}
                    <textarea {...commonProps} rows="4"></textarea>
                </div>
            ) : (
                <div className="relative">
                    {icon && (
                        <div className="pointer-events-none absolute left-0 top-0 flex items-center pl-3 h-10">
                            <Icon
                                name={icon}
                                className="h-5 w-5 text-gray-400"
                            />
                        </div>
                    )}
                    <input type={type} {...commonProps} />
                </div>
            )}
            {helpText && (
                <p className="text-xs text-gray-500 mt-1">{helpText}</p>
            )}
        </div>
    );
};

const CheckboxField = ({ label, id, checked, onChange, helpText = "" }) => (
    <div className="flex flex-col">
        <div className="flex items-center">
            <input
                id={id}
                name={id}
                type="checkbox"
                checked={checked}
                onChange={onChange}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor={id} className="ml-3 text-sm text-gray-700">
                {label}
            </label>
        </div>
        {helpText && <p className="ml-7 text-xs text-gray-500">{helpText}</p>}
    </div>
);

const SelectField = ({
    id,
    label,
    value,
    onChange,
    options,
    required,
    icon,
    helpText,
}) => {
    return (
        <div className="relative">
            <label htmlFor={id} className="text-gray-700 font-medium">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
                {icon && (
                    <div className="pointer-events-none absolute left-0 top-0 flex items-center pl-3 h-10">
                        <Icon name={icon} className="h-5 w-5 text-gray-400" />
                    </div>
                )}
                <select
                    id={id}
                    name={id}
                    value={value}
                    onChange={onChange}
                    required={required}
                    className={`block w-full ${
                        icon ? "pl-10" : "pl-4"
                    } pr-10 py-2 mt-2 text-gray-700 bg-white border border-gray-200 rounded-md focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 focus:outline-none focus:ring appearance-none`}
                >
                    {options?.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <Icon
                        name="ChevronDown"
                        className="h-5 w-5 text-gray-400"
                    />
                </div>
            </div>
            {helpText && (
                <p className="text-xs text-gray-500 mt-1">{helpText}</p>
            )}
        </div>
    );
};

const RadioGroup = ({ id, label, value, onChange, options, required }) => {
    return (
        <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-2">
                {label} {required && <span className="text-red-500">*</span>}
            </legend>
            <div className="space-y-2">
                {options?.map((option) => (
                    <div key={option.value} className="flex items-center">
                        <input
                            id={`${id}-${option.value}`}
                            name={id}
                            type="radio"
                            value={option.value}
                            checked={value === option.value}
                            onChange={onChange}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label
                            htmlFor={`${id}-${option.value}`}
                            className="ml-3 block text-sm text-gray-800"
                        >
                            {option.label}
                        </label>
                    </div>
                ))}
            </div>
        </fieldset>
    );
};

// Component metadata for AI system

export {
    FormField,
    CheckboxField,
    SelectField,
    RadioGroup,
    FormattedNumberInput,
};
